import { Method } from 'axios';
import Joi from 'joi';
import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansMetricsShareLinksType } from './useSextforcePublicMetric';

export interface OnlyFansMetricsShareOptions {
    // Trial Link
    claimsCount?: boolean;
    subscribeCounts?: boolean;
    // Tracking Link
    countSubscribers?: boolean;
    countTransitions?: boolean;
    // All
    subscribersGraph?: boolean;
    suspiciousUsers?: boolean;
    spendersCount?: boolean;
    // Earnings
    totalEarnings?: boolean;
    totalEarningsNet?: boolean;
    totalChargebacks?: boolean;
    totalChargebacksNet?: boolean;
    totalSubscription?: boolean;
    totalSubscriptionNet?: boolean;
    totalResubscription?: boolean;
    totalResubscriptionNet?: boolean;
    totalMessage?: boolean;
    totalMessageNet?: boolean;
    totalTip?: boolean;
    totalTipNet?: boolean;
    totalPost?: boolean;
    totalPostNet?: boolean;
    totalStream?: boolean;
    totalStreamNet?: boolean;
}

export const OnlyFansMetricsShareOptionsSchema = Joi.object<OnlyFansMetricsShareOptions>({
    claimsCount: Joi.boolean().default(false),
    subscribeCounts: Joi.boolean().default(false),
    countSubscribers: Joi.boolean().default(false),
    countTransitions: Joi.boolean().default(false),
    subscribersGraph: Joi.boolean().default(false),
    suspiciousUsers: Joi.boolean().default(false),
    spendersCount: Joi.boolean().default(false),
    totalEarnings: Joi.boolean().default(false),
    totalEarningsNet: Joi.boolean().default(false),
    totalChargebacks: Joi.boolean().default(false),
    totalChargebacksNet: Joi.boolean().default(false),
    totalSubscription: Joi.boolean().default(false),
    totalSubscriptionNet: Joi.boolean().default(false),
    totalResubscription: Joi.boolean().default(false),
    totalResubscriptionNet: Joi.boolean().default(false),
    totalMessage: Joi.boolean().default(false),
    totalMessageNet: Joi.boolean().default(false),
    totalTip: Joi.boolean().default(false),
    totalTipNet: Joi.boolean().default(false),
    totalPost: Joi.boolean().default(false),
    totalPostNet: Joi.boolean().default(false),
    totalStream: Joi.boolean().default(false),
    totalStreamNet: Joi.boolean().default(false),
}).options({ stripUnknown: true });

export const createEmptyOnlyFansMetricsShareOptions = (): OnlyFansMetricsShareOptions => {
    return {
        claimsCount: false,
        subscribeCounts: false,
        countSubscribers: false,
        countTransitions: false,
        subscribersGraph: false,
        suspiciousUsers: false,
        spendersCount: false,
        totalEarnings: false,
        totalEarningsNet: false,
        totalChargebacks: false,
        totalChargebacksNet: false,
        totalSubscription: false,
        totalSubscriptionNet: false,
        totalResubscription: false,
        totalResubscriptionNet: false,
        totalMessage: false,
        totalMessageNet: false,
        totalTip: false,
        totalTipNet: false,
        totalPost: false,
        totalPostNet: false,
        totalStream: false,
        totalStreamNet: false,
    };
};

export interface OnlyFansMetricsWebHook {
    _id?: string;
    metricId: string;
    type: OnlyFansMetricsShareLinksType;
    agencyPartnerId?: string;
    agencyPartnerMetricId?: string | number;
    name?: string;
    url?: string;
    method?: Method;
    headers?: { [key: string]: string };
    parameters?: { [key: string]: string };
    options: OnlyFansMetricsShareOptions;
    active: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    deletedDate?: Date;
    lastUpdateDate?: Date;
}

export const OnlyFansMetricsWebHookSchema = Joi.object<OnlyFansMetricsWebHook>({
    _id: Joi.string().optional(),
    metricId: Joi.string().required(),
    type: Joi.string().valid('metric', 'metricsGroup').required(),
    agencyPartnerId: Joi.string().optional(),
    agencyPartnerMetricId: Joi.alternatives().try(Joi.string(), Joi.number()).optional(),
    name: Joi.string().optional(),
    url: Joi.string().optional(),
    method: Joi.string().valid('GET', 'POST', 'PUT', 'DELETE').optional(),
    headers: Joi.object().optional(),
    parameters: Joi.object().optional(),
    options: OnlyFansMetricsShareOptionsSchema.required(),
    active: Joi.boolean().required(),
    deletedDate: Joi.date().optional(),
    lastUpdateDate: Joi.date().optional(),
}).options({ stripUnknown: true });

const useSextforceMetricsWebhook = (metricId: string, metricType: string) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const params = useParams();
    const axios = useAxios();

    const [isCreatingNewWebhook, setIsCreatingNewWebhook] = useState<boolean>(false);
    const [isUpdatingWebhook, setIsUpdatingWebhook] = useState<boolean>(false);
    const [isDeletingWebhook, setIsDeletingWebhook] = useState<boolean>(false);

    // Fetch Webhooks for a specific metric
    const fetchWebhooks = async (metricId: string, metricType: string): Promise<OnlyFansMetricsWebHook[]> => {
        const query: string = `${settingsContext.routes.metrics.base}${params.userId}/${
            metricType === 'promoCampaign' ? 'promocampaigns' : 'trials'
        }/${metricId}/webhooks`;

        // Update campaign settings
        const data = await axios
            .get(query)
            .then(async response => response.data as OnlyFansMetricsWebHook[])
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);

                return [];
            });

        return data;
    };

    // Create a new webhook
    const createWebhook = async (webhook: OnlyFansMetricsWebHook): Promise<OnlyFansMetricsWebHook | null> => {
        const query: string = `${settingsContext.routes.metrics.base}${params.userId}/${
            metricType === 'promoCampaign' ? 'promocampaigns' : 'trials'
        }/${metricId}/webhooks`;

        setIsCreatingNewWebhook(true);

        const data = await axios
            .post(query, webhook)
            .then(async response => {
                const data = response.data as OnlyFansMetricsWebHook;
                return data;
            })
            .catch(error => {
                console.error(error);

                handleHttpError(error, dialog);

                return null;
            })
            .finally(() => {
                setIsCreatingNewWebhook(false);
            });

        return data;
    };

    // Update a webhook
    const updateWebhook = async (webhook: OnlyFansMetricsWebHook): Promise<OnlyFansMetricsWebHook | null> => {
        const query: string = `${settingsContext.routes.metrics.base}${params.userId}/${
            metricType === 'promoCampaign' ? 'promocampaigns' : 'trials'
        }/${metricId}/webhooks/${webhook._id}`;

        setIsUpdatingWebhook(true);

        const data = await axios
            .put(query, webhook)
            .then(async response => {
                if (response.status !== 200) {
                    throw new Error('Failed to update webhook');
                }

                const data = response.data as OnlyFansMetricsWebHook;

                return data;
            })
            .catch(error => {
                console.error(error);

                handleHttpError(error, dialog);

                return null;
            })
            .finally(() => {
                setIsUpdatingWebhook(false);
            });

        return data;
    };

    // Delete a webhook
    const deleteWebhook = async (webhookId: string): Promise<boolean> => {
        const query: string = `${settingsContext.routes.metrics.base}${params.userId}/${
            metricType === 'promoCampaign' ? 'promocampaigns' : 'trials'
        }/${metricId}/webhooks/${webhookId}`;

        setIsDeletingWebhook(true);

        return await axios
            .delete(query)
            .then(response => {
                console.log(response);
                if (response.status === 200 && response.data?.success) {
                    return true;
                } else {
                    return false;
                }
            })
            .catch(error => {
                console.error(error);

                handleHttpError(error, dialog);

                return false;
            })
            .finally(() => {
                setIsDeletingWebhook(false);
            });
    };

    const query = useQuery(['metricsWebhooks', userContext.jwtToken, params.userId, metricId], () => fetchWebhooks(metricId, metricType), {
        refetchOnWindowFocus: false,
        staleTime: 0,
        enabled: userContext.jwtToken && settingsContext.apiKey && params.userId && metricId ? true : false,
    });

    return { ...query, isCreatingNewWebhook, createWebhook, updateWebhook, isUpdatingWebhook, isDeletingWebhook, deleteWebhook };
};

export default useSextforceMetricsWebhook;
